import * as XLSX from 'xlsx';

/**
 * Service for parsing Excel and CSV files to extract DevTip data
 */
class FileParserService {
  /**
   * Parse Excel file and extract DevTip data
   * @param {File} file - Excel file (.xlsx, .xls)
   * @param {string} sheetName - Name of the sheet to parse (optional, defaults to first sheet)
   * @returns {Promise<Array>} Array of DevTip objects
   */
  async parseExcelFile(file, sheetName = null) {
    console.log('=== FILEPARSER: parseExcelFile START ===');
    console.log('File name:', file.name);
    console.log('File size:', file.size);
    console.log('Requested sheet:', sheetName);

    try {
      console.log('Converting file to buffer...');
      const buffer = await this.constructor.fileToBuffer(file);
      console.log('Buffer created, size:', buffer.byteLength);

      console.log('Reading Excel workbook...');
      const workbook = XLSX.read(buffer, { type: 'buffer' });
      console.log('Workbook read successfully');
      console.log('Available sheets in workbook:', workbook.SheetNames);

      // Use specified sheet or default to first sheet
      const targetSheetName = sheetName || workbook.SheetNames[0];
      console.log('Target sheet name:', targetSheetName);

      if (!workbook.SheetNames.includes(targetSheetName)) {
        throw new Error(
          `Sheet '${targetSheetName}' not found. Available sheets: ${workbook.SheetNames.join(
            ', ',
          )}`,
        );
      }

      console.log('Getting worksheet...');
      const worksheet = workbook.Sheets[targetSheetName];

      if (!worksheet) {
        throw new Error('No worksheet found in Excel file');
      }
      console.log('Worksheet retrieved successfully');
      console.log('Worksheet type:', typeof worksheet);
      console.log('Worksheet constructor:', worksheet.constructor.name);

      console.log('Converting worksheet to JSON...');
      console.log('Worksheet object keys:', Object.keys(worksheet));
      console.log('Worksheet range:', worksheet['!ref']);

      // Calculate actual data range to avoid processing empty cells
      console.log('Calculating actual data range...');
      const keys = Object.keys(worksheet).filter((key) => key !== '!ref');
      if (keys.length === 0) {
        throw new Error('Worksheet has no data');
      }

      // Find the actual data boundaries
      const columnRegex = /^[A-Z]+/;
      const rowRegex = /\d+$/;

      let minCol = 'A';
      let maxCol = 'A';
      let minRow = 1;
      let maxRow = 1;

      keys.forEach((key) => {
        const col = key.match(columnRegex)?.[0];
        const row = parseInt(key.match(rowRegex)?.[0], 10);

        if (col && row) {
          if (col < minCol) minCol = col;
          if (col > maxCol) maxCol = col;
          if (row < minRow) minRow = row;
          if (row > maxRow) maxRow = row;
        }
      });

      const actualRange = `${minCol}${minRow}:${maxCol}${maxRow}`;
      console.log('Actual data range:', actualRange);
      console.log('Original range:', worksheet['!ref']);

      // Create a new worksheet with only the actual data range
      const limitedWorksheet = {};
      limitedWorksheet['!ref'] = actualRange;

      // Copy only the cells within the actual range
      keys.forEach((key) => {
        if (worksheet[key]) {
          limitedWorksheet[key] = worksheet[key];
        }
      });

      console.log('Limited worksheet keys count:', Object.keys(limitedWorksheet).length);

      // Add timeout protection for the JSON conversion
      console.log('About to start JSON conversion with limited worksheet...');

      const jsonConversionPromise = new Promise((resolve, reject) => {
        try {
          console.log('Starting XLSX.utils.sheet_to_json with limited range...');

          const result = XLSX.utils.sheet_to_json(limitedWorksheet, { header: 1 });
          console.log('XLSX conversion completed');
          resolve(result);
        } catch (error) {
          console.error('XLSX conversion error:', error);
          reject(error);
        }
      });

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('JSON conversion timed out after 15 seconds'));
        }, 15000);
      });

      console.log('Starting Promise.race for JSON conversion...');
      const jsonData = await Promise.race([jsonConversionPromise, timeoutPromise]);
      console.log('JSON conversion completed, rows:', jsonData.length);

      if (jsonData.length < 2) {
        throw new Error('Excel file must have at least a header row and one data row');
      }

      const headers = jsonData[0];
      const dataRows = jsonData.slice(1);
      console.log('Headers:', headers);
      console.log('Data rows count:', dataRows.length);

      console.log('Validating headers...');
      this.constructor.validateHeaders(headers);
      console.log('Headers validation passed');

      console.log('Parsing Excel rows...');
      const result = this.parseExcelRows(dataRows, headers);
      console.log('Excel rows parsing completed, result count:', result.length);
      console.log('=== FILEPARSER: parseExcelFile SUCCESS END ===');
      return result;
    } catch (error) {
      console.error('Error processing Excel file:', error);
      throw new Error(`Failed to process Excel file: ${error.message}`);
    }
  }

  /**
   * Parse CSV file and extract DevTip data
   * @param {File} file - CSV file
   * @returns {Promise<Array>} Array of DevTip objects
   */
  async parseCsvFile(file) {
    try {
      const text = await this.constructor.fileToText(file);
      const lines = text.split('\n').filter((line) => line.trim());

      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row');
      }

      const headers = this.constructor.parseCsvLine(lines[0]);
      const dataRows = lines.slice(1).map((line) => this.constructor.parseCsvLine(line));

      this.constructor.validateHeaders(headers);

      return this.parseCsvRows(dataRows, headers);
    } catch (error) {
      console.error('Error processing CSV file:', error);
      throw new Error(`Failed to process CSV file: ${error.message}`);
    }
  }

  /**
   * Convert file to buffer for Excel processing
   * @param {File} file - File object
   * @returns {Promise<ArrayBuffer>} File buffer
   */
  static fileToBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Convert file to text for CSV processing
   * @param {File} file - File object
   * @returns {Promise<string>} File text content
   */
  static fileToText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  /**
   * Validate that required headers are present
   * @param {Array} headers - Array of header strings
   */
  static validateHeaders(headers) {
    const requiredHeaders = ['title_en', 'title_de', 'text_en', 'text_de', 'dimensionValue'];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.some((h) => h.toLowerCase().trim() === header.toLowerCase().trim()),
    );

    if (missingHeaders.length > 0) {
      throw new Error(
        `Missing required headers: ${missingHeaders.join(', ')}. ` +
          `Required headers: ${requiredHeaders.join(', ')}`,
      );
    }
  }

  /**
   * Parse Excel rows into DevTip objects
   * @param {Array} dataRows - Array of data rows
   * @param {Array} headers - Array of header strings
   * @returns {Array} Array of DevTip objects
   */
  parseExcelRows(dataRows, headers) {
    console.log('=== FILEPARSER: parseExcelRows START ===');
    console.log('Data rows to process:', dataRows.length);

    const devTips = [];

    for (let i = 0; i < dataRows.length; i += 1) {
      const row = dataRows[i];
      console.log(`Processing row ${i + 2}...`);

      // Check if row has any meaningful data (not completely empty)
      if (row && row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
        try {
          // +2 because we start from row 2 (after header)
          const devTipResults = this.constructor.parseRow(row, headers, i + 2);
          if (devTipResults && Array.isArray(devTipResults)) {
            // parseRow now returns an array of DevTip objects (one per dimension)
            devTipResults.forEach((devTip) => {
              if (devTip) {
                devTips.push(devTip);
              }
            });
            console.log(`Row ${i + 2} processed successfully - created ${devTipResults.length} DevTips`);
          } else {
            console.log(`Row ${i + 2} returned invalid result:`, devTipResults);
          }
        } catch (error) {
          console.warn(`Skipping row ${i + 2}: ${error.message}`);
        }
      } else {
        console.log(`Row ${i + 2} is empty, skipping`);
      }
    }

    console.log('Total tips parsed:', devTips.length);
    console.log('=== FILEPARSER: parseExcelRows END ===');

    // Log summary of what was processed vs what was returned
    console.log('=== IMPORT SUMMARY ===');
    console.log(`Total rows in Excel: ${dataRows.length}`);
    console.log(`Rows with data: ${devTips.length}`);
    console.log(`Skipped rows: ${dataRows.length - devTips.length}`);

    return devTips;
  }

  /**
   * Parse CSV rows into DevTip objects
   * @param {Array} dataRows - Array of data rows
   * @param {Array} headers - Array of header strings
   * @returns {Array} Array of DevTip objects
   */
  parseCsvRows(dataRows, headers) {
    const devTips = [];

    for (let i = 0; i < dataRows.length; i += 1) {
      const row = dataRows[i];
      // Check if row has any meaningful data (not completely empty)
      if (row && row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
        try {
          // +2 because we start from row 2 (after header)
          const devTipResults = this.constructor.parseRow(row, headers, i + 2);
          if (devTipResults && Array.isArray(devTipResults)) {
            // parseRow now returns an array of DevTip objects (one per dimension)
            devTipResults.forEach((devTip) => {
              if (devTip) {
                devTips.push(devTip);
              }
            });
          }
        } catch (error) {
          console.warn(`Skipping row ${i + 2}: ${error.message}`);
        }
      }
    }

    // Log summary of what was processed vs what was returned
    console.log('=== CSV IMPORT SUMMARY ===');
    console.log(`Total rows in CSV: ${dataRows.length}`);
    console.log(`Rows with data: ${devTips.length}`);
    console.log(`Skipped rows: ${dataRows.length - devTips.length}`);

    return devTips;
  }

  /**
   * Parse a single row into a DevTip object
   * @param {Array} row - Data row
   * @param {Array} headers - Array of header strings
   * @param {number} rowNumber - Row number for error reporting
   * @returns {Object|null} DevTip object or null if invalid
   */
  static parseRow(row, headers, rowNumber) {
    console.log(`=== FILEPARSER: parseRow START for row ${rowNumber} ===`);
    console.log('Row data:', row);
    console.log('Headers:', headers);

    const headerMap = new Map();
    headers.forEach((header, index) => {
      headerMap.set(header.toLowerCase().trim(), index);
    });
    console.log('Header map created');

    const getValue = (headerName) => {
      const index = headerMap.get(headerName.toLowerCase().trim());
      if (index === undefined || index >= row.length) {
        return '';
      }
      const value = row[index];
      return value ? String(value).trim() : '';
    };

    const titleEn = getValue('title_en');
    const titleDe = getValue('title_de');
    const textEn = getValue('text_en');
    const textDe = getValue('text_de');
    const dimensionValue = getValue('dimensionValue');
    const activeStr = getValue('active');

    // Handle multi-dimensional values (comma/semicolon separated)
    let dimensionValues = [];
    if (dimensionValue) {
      // Split by comma or semicolon and trim whitespace
      dimensionValues = dimensionValue
        .split(/[,;]/)
        .map((dim) => dim.trim())
        .filter((dim) => dim.length > 0); // Remove empty dimensions

      console.log(`Row ${rowNumber} - Original dimension: "${dimensionValue}", Parsed dimensions:`, dimensionValues);
    }

    // Validate required fields (titles are optional)
    if (!textEn || !textDe || dimensionValues.length === 0) {
      throw new Error(`Missing required fields in row ${rowNumber}. Found ${dimensionValues.length} dimensions: [${dimensionValues.join(', ')}]`);
    }

    // Parse active field (default to true if not specified)
    let active = true;
    if (activeStr) {
      const activeLower = activeStr.toLowerCase();
      if (activeLower === 'false' || activeLower === '0' || activeLower === 'no') {
        active = false;
      } else if (activeLower === 'true' || activeLower === '1' || activeLower === 'yes') {
        active = true;
      } else {
        console.warn(`Invalid active value '${activeStr}' in row ${rowNumber}, defaulting to true`);
      }
    }

    // Transform to match the backend expected format
    // Create one DevTip object per dimension
    const results = dimensionValues.map((dimValue) => {
      const devTip = {
        text: { en: textEn, de: textDe },
        dimensionValue: dimValue,
        active,
      };

      // Only include title if at least one language has content
      if (titleEn || titleDe) {
        devTip.title = {};
        if (titleEn) devTip.title.en = titleEn;
        if (titleDe) devTip.title.de = titleDe;
      }

      return devTip;
    });

    console.log(`Row ${rowNumber} results:`, results);
    console.log(`=== FILEPARSER: parseRow END for row ${rowNumber} ===`);
    return results;
  }

  /**
   * Parse CSV line, handling quoted values
   * @param {string} line - CSV line string
   * @returns {Array} Array of field values
   */
  static parseCsvLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  /**
   * Get supported file types
   * @returns {Array} Array of supported file extensions
   */
  static getSupportedFileTypes() {
    return ['.xlsx', '.xls', '.csv'];
  }

  /**
   * Get sheet names from Excel file
   * @param {File} file - Excel file
   * @returns {Promise<Array>} Array of sheet names
   */
  async getExcelSheetNames(file) {
    try {
      const buffer = await this.constructor.fileToBuffer(file);
      const workbook = XLSX.read(buffer, { type: 'buffer' });
      return workbook.SheetNames;
    } catch (error) {
      throw new Error(`Failed to read Excel file: ${error.message}`);
    }
  }

  /**
   * Check if file type is supported
   * @param {File} file - File object
   * @returns {boolean} True if file type is supported
   */
  isFileTypeSupported(file) {
    const supportedTypes = this.constructor.getSupportedFileTypes();
    return supportedTypes.some((ext) => file.name.toLowerCase().endsWith(ext));
  }
}

export default new FileParserService();
